// Buy box for a product with optional service add-ons. Builds the price summary
// client-side for instant feedback, then submits the main variant plus any
// chosen add-ons in a single multi-item /cart/add.js call via <cart-drawer>.
//
// About the bundle discount: this only DISPLAYS the reduction. Shopify prices
// the cart from the products themselves, so the matching reduction has to exist
// as an automatic discount in Admin (Discounts → automatic → the same
// percentage off the Serviceaftaler collection when bought with a tank or
// system). The two must always carry the same rate — the theme's is set on the
// product section, Shopify's in Admin. If that discount is not
// configured, set the section's discount to 0 % so the page never advertises a
// price the checkout will not honour. This is documented in SEED-GUIDE.md.

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.submitBtn = this.querySelector('[data-submit]');
    this.submitHint = this.querySelector('[data-submit-hint]');
    this.summaryRows = this.querySelector('[data-summary-rows]');
    this.summaryTotal = this.querySelector('[data-summary-total]');
    this.summaryNote = this.querySelector('[data-summary-note]');

    this.basePrice = Number(this.dataset.basePrice || 0);
    this.baseLabel = this.dataset.baseLabel || '';
    this.discountPercent = Number(this.dataset.discountPercent || 0);
    // 1 when Shopify's prices already include VAT — see snippets/tax-multiplier.
    this.taxMultiplier = Number(this.dataset.taxMultiplier) || 1;

    // One entry per picker present on the page. `chosen` stays false until the
    // visitor actively selects something — including an explicit "no thanks" —
    // which is what gates the submit button.
    this.pickers = {};
    this.querySelectorAll('option-select[data-name]').forEach((el) => {
      this.pickers[el.dataset.name] = { chosen: false, price: 0, label: '', period: 'one_time' };
    });

    this.addEventListener('option-select:change', (event) => {
      const picker = this.pickers[event.detail.name];
      if (!picker) return;
      picker.chosen = true;
      picker.price = Number(event.detail.price || 0);
      picker.label = event.detail.label || '';
      picker.period = event.detail.period || 'one_time';
      this.render();
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      if (this.isReady()) this.submit();
    });

    document.addEventListener('cart:added', () => this.resetButton());
    document.addEventListener('cart:error', () => this.resetButton());

    this.render();
  }

  isReady() {
    if (this.dataset.available === 'false') return false;
    return Object.values(this.pickers).every((p) => p.chosen);
  }

  // Rounded to whole minor units so the displayed reduction and the total can
  // never disagree by a rounding cent.
  agreementDiscount() {
    const agreement = this.pickers.agreement;
    if (!agreement || !this.discountPercent || agreement.price <= 0) return 0;
    return Math.round((agreement.price * this.discountPercent) / 100);
  }

  render() {
    this.renderSummary();
    this.renderButton();
  }

  renderSummary() {
    if (!this.summaryRows) return;

    const rows = [{ label: this.baseLabel, amount: this.basePrice, sign: '' }];
    let total = this.basePrice;
    let hasYearly = false;

    for (const name of ['startup', 'agreement']) {
      const picker = this.pickers[name];
      if (!picker || !picker.chosen || picker.price <= 0) continue;
      const yearly = picker.period === 'yearly';
      if (yearly) hasYearly = true;
      rows.push({
        label: picker.label,
        note: yearly ? this.perYearNote() : this.oneTimeNote(),
        amount: picker.price,
        sign: '+ ',
      });
      total += picker.price;
    }

    const discount = this.agreementDiscount();
    if (discount > 0) {
      rows.push({
        label: this.discountLabel(),
        amount: discount,
        sign: '− ',
        accent: true,
      });
      total -= discount;
    }

    this.summaryRows.innerHTML = rows
      .map(
        (row) => `
        <div class="product-page__summary-row${row.accent ? ' product-page__summary-row--accent' : ''}">
          <dt>${escapeHtml(row.label)}${row.note ? ` <em>${escapeHtml(row.note)}</em>` : ''}</dt>
          <dd>${row.sign}${escapeHtml(this.formatMoney(row.amount))}</dd>
        </div>`
      )
      .join('');

    if (this.summaryTotal) this.summaryTotal.textContent = this.formatMoney(total);
    this.summaryNote?.toggleAttribute('hidden', !hasYearly);
  }

  renderButton() {
    if (!this.submitBtn) return;
    const ready = this.isReady();
    this.submitBtn.disabled = !ready;
    this.submitHint?.toggleAttribute('hidden', ready);
  }

  // Strings come from data attributes so nothing user-facing is hardcoded here.
  perYearNote() {
    return this.dataset.perYearNote || '(pr. år)';
  }

  oneTimeNote() {
    return this.dataset.oneTimeNote || '(engangsbeløb)';
  }

  discountLabel() {
    const template = this.dataset.discountLabel || 'Rabat på serviceaftale ([percent] %)';
    return template.replace('[percent]', String(this.discountPercent));
  }

  // The browser's native Intl API with the store's own active currency and
  // locale — never a hand-rolled currency symbol or separator.
  //
  // Scaled to VAT-inclusive by the same factor snippets/price.liquid uses, so
  // the summary matches every other price on the page. The factor is 1 whenever
  // Shopify's figures already include VAT, which is why this is safe to apply
  // unconditionally here. Scaling the summed total rather than each row keeps
  // the rows and the total consistent to the øre.
  formatMoney(cents) {
    const gross = Math.round(cents * this.taxMultiplier);
    const locale = document.documentElement.lang || 'da-DK';
    const currency = window.Shopify?.currency?.active || 'DKK';
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(gross / 100);
    } catch (error) {
      return String(gross / 100);
    }
  }

  submit() {
    const quantityInput = this.form.querySelector('input[name="quantity"]');
    const quantity = Number(quantityInput?.value || 1);
    const items = [{ id: Number(this.dataset.variantId), quantity }];

    this.form.querySelectorAll('[data-addon-input]').forEach((input) => {
      if (input.value) items.push({ id: Number(input.value), quantity: 1 });
    });

    this.submitBtn.disabled = true;
    this.submitBtn.dataset.busy = 'true';
    const label = this.submitBtn.querySelector('[data-submit-label]');
    if (label) label.textContent = this.submitBtn.dataset.addingLabel;

    document.dispatchEvent(new CustomEvent('cart:add', { detail: { items } }));
  }

  resetButton() {
    if (!this.submitBtn) return;
    delete this.submitBtn.dataset.busy;
    const label = this.submitBtn.querySelector('[data-submit-label]');
    if (label) label.textContent = this.submitBtn.dataset.label;
    this.renderButton();
  }
}

function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]
  );
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
